import { z } from 'zod'

export const StoredComparisonTreeSchema = z.union([z.string(), z.object({
  encoding: z.literal('previous-tree-v1'),
  previous_length: z.number().int().nonnegative(),
  prefix: z.number().int().nonnegative(),
  suffix: z.number().int().nonnegative(),
  insert: z.string(),
})])
type StoredTree = z.infer<typeof StoredComparisonTreeSchema>
type TreePair<T> = { expected_tree: T; actual_tree: T }

export function createTreeDiffEncoder() {
  let previous: string | undefined
  return (tree: string): StoredTree => {
    let stored: StoredTree = tree
    if (previous !== undefined) {
      let prefix = 0
      const limit = Math.min(previous.length, tree.length)
      while (prefix < limit && previous[prefix] === tree[prefix]) prefix++
      let suffix = 0
      while (suffix < limit - prefix && previous[previous.length - 1 - suffix] === tree[tree.length - 1 - suffix]) suffix++
      const diff: StoredTree = {
        encoding: 'previous-tree-v1', previous_length: previous.length,
        prefix, suffix, insert: tree.slice(prefix, tree.length - suffix),
      }
      if (JSON.stringify(diff).length < JSON.stringify(tree).length) stored = diff
    }
    previous = tree
    return stored
  }
}

export function createTreeDiffDecoder() {
  let previous: string | undefined
  return (stored: StoredTree): string => {
    if (typeof stored === 'string') return (previous = stored)
    if (previous === undefined || previous.length !== stored.previous_length || stored.prefix + stored.suffix > previous.length) {
      throw new Error('Invalid comparison tree diff base or range')
    }
    previous = previous.slice(0, stored.prefix) + stored.insert + previous.slice(previous.length - stored.suffix)
    return previous
  }
}

// Each array owns independent expected/actual streams, visited parent then recovery. Full strings
// reset the stream, so legacy results and transitions where a diff would be larger remain readable.
function mapTrees<I, O, T extends TreePair<I> & { post_recovery?: TreePair<I> }>(
  points: readonly T[], expected: (tree: I) => O, actual: (tree: I) => O,
) {
  function mapPair<P extends TreePair<I>>(pair: P) {
    const { expected_tree, actual_tree, ...rest } = pair
    return { ...rest, expected_tree: expected(expected_tree), actual_tree: actual(actual_tree) }
  }
  return points.map(point => {
    const { post_recovery: omittedRecovery, ...rest } = point
    const recovery: T['post_recovery'] = omittedRecovery
    const mapped = mapPair(rest)
    return {
      ...mapped,
      ...(recovery ? { post_recovery: mapPair(recovery) } : {}),
    }
  })
}

export function encodeComparisonTrees<T extends TreePair<string> & { post_recovery?: TreePair<string> }>(points: readonly T[]) {
  return mapTrees(points, createTreeDiffEncoder(), createTreeDiffEncoder())
}

export function decodeComparisonTrees<T extends TreePair<StoredTree> & { post_recovery?: TreePair<StoredTree> }>(points: readonly T[]) {
  return mapTrees(points, createTreeDiffDecoder(), createTreeDiffDecoder())
}
